import { createContext, FC, ReactNode, useCallback, useContext, useEffect, useState } from 'react'
import { getUser } from 'services/client/interceptor.client'
import client from '../services/client'
import { BaseData } from 'types/base.type'
import { HttpErrorType } from 'types/http.type'
import { errorColor, errorMessage } from 'utils/error.util'
import { useSnackBar } from './snackbar.context'
import { useAuth } from 'react-oidc-context'

export type PromotoriaType = {
  id: number
  descricao: string
  temNotificacao: boolean
}

type PromotoriaContextProps = {
  promotorias: PromotoriaType[]
  promotoriaSelecionada?: PromotoriaType
  onChangePromotoriaSelecionada: (promotoria: PromotoriaType) => void
  onResearch: () => void
}

const localStoragePromotoria = `${getUser()?.profile.preferred_username}:promotoriaSelecionada`

const PromotoriaContext = createContext({} as PromotoriaContextProps)

type PromotoriaProviderProps = {
  children: ReactNode
}

const PromotoriaProvider: FC<PromotoriaProviderProps> = ({ children }) => {
  const { isAuthenticated } = useAuth()
  const { showSnackBar } = useSnackBar()

  const [promotorias, setPromotorias] = useState<PromotoriaType[]>([])
  const [promotoriaSelecionada, setPromotoriaSelecionada] = useState<PromotoriaType>()
  const [isResearching, setIsResearch] = useState<boolean>(false)

  const handleChangePromotoriaSelecionada = (value?: PromotoriaType) => {
    localStorage.setItem(localStoragePromotoria, JSON.stringify(value))
    setPromotoriaSelecionada(value)
  }

  useEffect(() => {
    const promotoriaSelecionadaJson = localStorage.getItem(localStoragePromotoria)
    if (promotorias.length > 0 && promotoriaSelecionadaJson) {
      const promotoriaSelecionadaObject = JSON.parse(promotoriaSelecionadaJson) as PromotoriaType
      const exists = promotorias.find((item) => item.id === promotoriaSelecionadaObject.id)
      if (exists) {
        setPromotoriaSelecionada(promotoriaSelecionadaObject)
      } else {
        handleChangePromotoriaSelecionada(promotorias[0])
      }
    } else {
      if (promotorias.length > 0) {
        handleChangePromotoriaSelecionada(promotorias[0])
      }
    }
  }, [promotorias])

  const toggleResearch = () => {
    if (!isResearching) setIsResearch(true)
  }

  const buscarPromotorias = useCallback(
    (isNotificacao?: boolean) => {
      if (isAuthenticated) {
        const url = `/promotoria`
        client
          .get<BaseData<PromotoriaType[]>>(url, { isNotificacao })
          .then((res) => setPromotorias(res.data))
          .catch((error: HttpErrorType) => {
            if (error.response.data?.message !== 'Sem promotorias vinculadas.') {
              showSnackBar(errorMessage(error), errorColor(error))
            }
          })
          .finally(() => setIsResearch(false))
      }
    },
    [isAuthenticated],
  )

  useEffect(() => {
    if (isResearching) {
      buscarPromotorias(true)
    }
  }, [isResearching])

  useEffect(() => {
    buscarPromotorias(true)
  }, [buscarPromotorias])

  return (
    <PromotoriaContext.Provider
      value={{
        promotorias: promotorias,
        promotoriaSelecionada: promotoriaSelecionada,
        onChangePromotoriaSelecionada: handleChangePromotoriaSelecionada,
        onResearch: toggleResearch,
      }}
    >
      {children}
    </PromotoriaContext.Provider>
  )
}

const usePromotoriaContext = () => {
  const { onChangePromotoriaSelecionada, ...props } = useContext(PromotoriaContext)
  const [openModalPromotoria, setOpenModalPromotoria] = useState<boolean>(false)

  const handleOpenModalPromotoria = () => {
    setOpenModalPromotoria(true)
  }

  const handleCloseModalPromotoria = (value?: PromotoriaType) => {
    setOpenModalPromotoria(false)
    if (value) {
      onChangePromotoriaSelecionada(value)
    }
  }

  return {
    ...props,
    openModalPromotoria,
    handleOpenModalPromotoria,
    handleCloseModalPromotoria,
  }
}

export { PromotoriaProvider, usePromotoriaContext }
